import { useListSearchState } from "common/hooks";
import { Feature } from "common/types";
import { useFetchQuery } from "common/queries/hooks";

import { Paths } from "client/routes";
import { useFeatures } from "client/clinics/hooks";
import { useProductFilterOptionState, useProductSearchQuery } from "client/products/hooks";
import { collectionFilterScope } from "client/products/hooks/constants";
import { SelectedProductFilters } from "client/products/types";

import { StorefrontCollection } from "../types";
import { SEARCH_DEFAULTS, SEARCH_DEFAULTS_WITH_PANEL, getSelectedFilters } from "../utils";
import { keys, urls } from "./constants";

export const useStorefrontCollectionQuery = (
  slug: string,
  { enabled = true }: { enabled?: boolean } = {},
) =>
  useFetchQuery<StorefrontCollection>(keys.detail(slug), {
    fetch: { url: urls.storefrontCollection(slug) },
    config: { enabled: enabled && !!slug },
  });

export const useCollectionSearch = (collection: StorefrontCollection) => {
  const isFiltersV2 = useFeatures()[Feature.StorefrontFiltersV2];
  const [search, updateSearch] = useListSearchState<Paths["productCollections"]["detail"]>(
    isFiltersV2 ? SEARCH_DEFAULTS_WITH_PANEL : SEARCH_DEFAULTS,
  );

  const filterOptionsScope = collectionFilterScope(collection.slug);
  const selectedFilters = getSelectedFilters(search, collection.show_filters);

  const {
    data: products,
    isLoading,
    isRefetching,
    error,
  } = useProductSearchQuery(
    {
      q: "",
      page: Number(search.page) || 1,
      page_size: search.page_size,
      sort: search.sort,
      collection: collection.slug,
      ...selectedFilters,
    },
    { filterOptionsScope },
  );

  const { data: cachedFilterOptions } = useProductFilterOptionState(filterOptionsScope);

  return {
    products,
    isLoading,
    isRefetching,
    error,
    /** Frozen at the collection's first response, used when later responses narrow the options. */
    cachedFilterOptions: cachedFilterOptions?.filters,
    selectedFilters,
    applyFilters: (next: Partial<SelectedProductFilters>) => updateSearch({ ...next, page: 1 }),
    goToPage: (nextPage: number) => {
      updateSearch({ page: nextPage });
      // Matches ProductSearchPage; without it page 2 opens scrolled to the bottom.
      window.scrollTo(0, 0);
    },
  };
};
